import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';
import LocalStorage from '../fw/local-storage';
import AirPlay from '../cast/airplay';
import AdsIma from '../ads/ima-ads';
import AdsRmpVast from '../ads/rmp-vast';
import AdBlocker from '../ads/ad-blocker';


export default class ReadyManagement {

  #rmp;
  #timeReady = -1;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get timeReady() {
    return this.#timeReady;
  }

  #onImaDaiAvailable() {
    Logger.print('ima-dai', 'Google HTML5 IMA DAI SDK available at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
    this.#rmp.adParserLoaded = true;
    this.#rmp.rmpAdsHelpers.createControlBarAdInfo();
    this.fire();
  }

  #imaDaiError() {
    Logger.print('ima-dai', 'could not load IMA SDK library - at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
    this.#rmp.adParserLoadError = true;
    this.#rmp.ads = false;
    this.fire();
    this.#rmp.dispatch('adblock');
  }

  #readyAds() {
    if (this.#rmp.adImaDai) {
      if (typeof google !== 'undefined' && typeof google.ima !== 'undefined' && typeof google.ima.dai !== 'undefined') {
        this.#onImaDaiAvailable();
      } else {
        const callScript = 'https://imasdk.googleapis.com/js/sdkloader/ima3_dai.js';
        FW.getScript(
          callScript,
          this.#onImaDaiAvailable.bind(this),
          this.#imaDaiError.bind(this),
          this.#rmp.shadowRoot
        );
      }
    } else if (this.#rmp.adParser === 'ima') {
      this.#rmp.rmpAdsIma = new AdsIma(this.#rmp);
      this.#rmp.rmpAdsIma.init();
    } else {
      this.#rmp.rmpAdsRmpVast = new AdsRmpVast(this.#rmp);
      this.#rmp.rmpAdsRmpVast.init();
    }
  }

  #dispatchReadyEvent() {
    this.#timeReady = FW.getNow() - this.#rmp.refTime;
    Logger.print('info', 'ready at ' + this.#timeReady + ' ms');
    this.#rmp.playerReady = true;
    this.#rmp.dispatch('ready');
    if (this.#rmp.frameRate > -1) {
      this.#rmp.dispatch('framerateavailable');
    }
    if (this.#rmp.ads) {
      AdBlocker.hasAdblock().then(adBlockerDetected => {
        if (adBlockerDetected) {
          this.#rmp.dispatch('adblock');
        }
      });
    }
  }

  #restoreQuality() {
    this.#rmp.one('playing', () => {
      if (LocalStorage.available(this.#rmp.allowLocalStorage)) {
        try {
          if (localStorage.getItem('rmpQuality') !== null) {
            let rmpQuality = JSON.parse(localStorage.getItem('rmpQuality'));
            if (rmpQuality.value && rmpQuality.value > -1) {
              let quality = parseInt(rmpQuality.value);
              const currentQualities = this.#rmp.qualities;
              if (quality > currentQualities.length - 1) {
                quality = currentQualities.length - 1;
              }
              if (quality === -1 && this.#rmp.readingPDorNative) {
                quality = 0;
              }
              Logger.print('local-storage', 'set quality from rememberQuality ' + quality);
              this.#rmp.quality = quality;
            }
          }
        } catch (warning) {
          console.warn(warning);
        }
      }
    });
  }

  #manageOffsetPosition() {
    // recover value for rememberCurrentTime 
    if (this.#rmp.rememberCurrentTime && LocalStorage.available(this.#rmp.allowLocalStorage)) {
      const id = this.#rmp.contentMetadata.id || 'default';
      try {
        if (localStorage.getItem('rmpCurrentTime-' + id) !== null) {
          let rmpCurrentTime = JSON.parse(localStorage.getItem('rmpCurrentTime-' + id));
          if (rmpCurrentTime.value) {
            this.#rmp.offsetStartPosition = parseInt(rmpCurrentTime.value);
            Logger.print('local-storage', 'read and set current time ' + this.#rmp.offsetStartPosition);
          }
        }
      } catch (warning) {
        console.warn(warning);
      }
    }

    // offsetStartPosition
    if (this.#rmp.offsetStartPosition > 0 && this.#rmp.isVod && !this.#rmp.ads) {
      this.#rmp.one('loadedmetadata', () => {
        if (this.#rmp.dom.video && this.#rmp.dom.video.seekable.length > 0) {
          this.#rmp.seekTo(this.#rmp.offsetStartPosition).catch(error => {
            this.#rmp.rmpUtils.warning(1016, error);
          });
        }
      });
    }
  }

  #setInitialVolume(inputVolume) {
    if (inputVolume === 0) {
      this.#rmp.mute = true;
    } else if (inputVolume < 1 && inputVolume > 0) {
      this.#rmp.volume = inputVolume;
    } else if (inputVolume === 1) {
      this.#rmp.volume = inputVolume;
      this.#rmp.onVolumeChangeFn();
    }
  }

  #manageInitialVolume() {
    const initialMuted = this.#rmp.muted;
    if (this.#rmp.initialVolume > 1) {
      this.#rmp.initialVolume = 1;
    } else if (this.#rmp.initialVolume < 0) {
      this.#rmp.initialVolume = 0;
    }
    if (this.#rmp.forceInitialVolume) {
      this.#rmp.volume = this.#rmp.initialVolume;
    } else {
      if (this.#rmp.rememberVolume && LocalStorage.available(this.#rmp.allowLocalStorage)) {
        try {
          if (localStorage.getItem('rmpVolume') !== null) {
            const rmpVolume = JSON.parse(localStorage.getItem('rmpVolume'));
            if (rmpVolume.value) {
              const volumeValue = parseFloat(rmpVolume.value);
              Logger.print('local-storage', 'read and set volume ' + volumeValue);
              this.#setInitialVolume(volumeValue);
            }
          }
        } catch (warning) {
          console.warn(warning);
        }
      } else {
        this.#setInitialVolume(this.#rmp.initialVolume);
      }
    }
    if (initialMuted) {
      this.#rmp.mute = true;
    }
  }

  #customModuleFilter() {
    const validCustomModules = [];
    this.#rmp.customModule.forEach(element => {
      if (typeof element.hint === 'string' && typeof element.svg === 'string' &&
        typeof element.callback === 'function') {
        if (typeof element.svgHover !== 'string') {
          element.svgHover = element.svg;
        }
        validCustomModules.push(element);
      }
    });
    this.#rmp.customModule = validCustomModules;
  }

  #customModuleInteraction(callback, event) {
    FW.eventStopPrevent(event);
    this.#rmp.rmpUIManagement.showChrome();
    callback();
  }

  #customModuleInit() {
    this.#rmp.customModule.forEach(element => {
      const type = element.hint;
      const svg = element.svg;
      const svgHover = element.svgHover;
      const callback = element.callback;
      let customClassName = type.toLowerCase();
      customClassName = customClassName.replace(/\s+/g, '-');
      this.#rmp.dom[type] = FW.createElement(
        'button',
        'rmp-button rmp-' + customClassName + ' rmp-module rmp-color-bg',
        null,
        null
      );
      const innerDiv = FW.createElement('div', 'rmp-module-button');
      const icon = FW.createElement('span', 'rmp-custom-module-icon');
      let css = `.rmp-${customClassName} .rmp-custom-module-icon {background-image: url(${svg});}`;
      css += `.rmp-${customClassName} .rmp-custom-module-icon:hover {background-image: url(${svgHover});}`;
      FW.appendStyle(css, this.#rmp.shadowRoot);
      innerDiv.appendChild(icon);
      this.#rmp.dom[type].appendChild(innerDiv);
      this.#rmp.dom.moduleArea.appendChild(this.#rmp.dom[type]);
      this.#rmp.rmpModuleManagement.appendHint(type, type);
      this.#rmp.rmpAccessible.button(this.#rmp.dom[type], type, true);
      const customModuleInteraction = this.#customModuleInteraction.bind(this, callback);
      FW.addEvents(['touchend', 'click'], this.#rmp.dom[type], customModuleInteraction);
      this.#rmp.dom[type].addEventListener('dblclick', FW.stopPropagation);
    });
  }

  updateOverlayModuleSelection() {
    // empty .rmp-overlay-menu-selection if any
    const existingOverlayMenuSelection = this.#rmp.dom.container.querySelectorAll('.rmp-overlay-menu-selection');
    if (existingOverlayMenuSelection.length > 0) {
      for (let i = 0, len = existingOverlayMenuSelection.length; i < len; i++) {
        FW.emptyElement(existingOverlayMenuSelection[i]);
      }
    }
    this.#rmp.rmpModuleManagement.overlayValidated = [];
    this.#rmp.rmpModuleManagement.appendModuleSelection();
  }

  minimalFire() {
    if (this.#rmp.playerReady) {
      return;
    }
    this.#dispatchReadyEvent();
    this.#rmp.resize();
  }

  fire() {
    if (this.#rmp.playerReady) {
      return;
    }
    let introText = '00:00';
    if (this.#rmp.dom.time && this.#rmp.dom.timeElapsedText && !this.#rmp.tvPlayerSkin) {
      FW.setText(this.#rmp.dom.timeElapsedText, introText);
    }
    // in case preload === auto we may already have a duration - do not replace in such case
    if (this.#rmp.dom.durationText && !this.#rmp.dom.durationText.textContent) {
      FW.setText(this.#rmp.dom.durationText, introText);
    }
    if (Env.isIos[0] || Env.isIpadOS) {
      this.#rmp.initialVolume = 1;
      this.#rmp.rememberVolume = false;
    }
    if (this.#rmp.rememberQuality && (this.#rmp.readingOutstream || this.#rmp.readingPDorNative)) {
      this.#rmp.rememberQuality = false;
      Logger.print('local-storage', 'cannot remember quality for HLS/MPEG-DASH native streaming or outstream ads');
    }

    this.#manageOffsetPosition();

    // rememberQuality
    if (this.#rmp.rememberQuality) {
      this.#restoreQuality();
    }

    // update overlay module selection on playing
    this.#rmp.updateOverlayModuleSelectionFn = this.updateOverlayModuleSelection.bind(this);
    this.#rmp.one('playing', this.#rmp.updateOverlayModuleSelectionFn);

    // fire ready here
    this.#dispatchReadyEvent();
    this.#rmp.resolvePromise('init');

    // needs ready to happen before calling rmpSizeManagement.resize
    this.#rmp.resize();

    // muted on start - needs ready to happen before setting volume or mute
    this.#manageInitialVolume();

    // needs ready to happen before calling initiatePlayback
    if (this.#rmp.autoplay) {
      // on mobile and macOS Safari for muted autoplay or autoplay with sound
      // volumechange event does not fire on video tag
      this.#rmp.onVolumeChangeFn();
      this.#rmp.rmpUtils.initialAutoplayRequest = true;
      this.#rmp.initiatePlayback();
    } else {
      this.#rmp.rmpUIManagement.showCentralPlay();
    }

    // load vtt.js after ready so that it can be loaded async
    if (this.#rmp.hasExternalCCFiles && !Env.isIos[0] && this.#rmp.rmpVttJS) {
      this.#rmp.rmpVttJS.init();
    }
  }

  init() {
    if (!this.#rmp.audioOnly) {
      this.#rmp.rmpPosterManagement.init();
    }
    if (this.#rmp.enableMatomoTracking && typeof window._paq !== 'undefined') {
      Logger.print('matomo', 'adding HTML5 video/audio tracking for container with id: ' + this.#rmp.id);
      window._paq.push(['MediaAnalytics::scanForMedia', document.getElementById(this.#rmp.id)]);
    }
    if (this.#rmp.airplay) {
      this.#rmp.rmpAirPlay = new AirPlay(this.#rmp);
      this.#rmp.rmpAirPlay.init();
    }
    this.#rmp.rmpLogo.filter();
    if (this.#rmp.logoConfiguration.imgUri !== '') {
      this.#rmp.rmpLogo.init();
    }
    if (this.#rmp.contentMetadata.title !== '') {
      this.#rmp.rmpUIManagement.appendContentTitle();
      if (this.#rmp.contentMetadata.description !== '') {
        this.#rmp.rmpUIManagement.appendContentDescription();
      }
    }
    if (Array.isArray(this.#rmp.customModule) && this.#rmp.customModule.length > 0 && this.#rmp.customModule[0]) {
      this.#customModuleFilter();
      if (this.#rmp.customModule.length > 0) {
        this.#customModuleInit();
      }
      Logger.print('info', 'filtered custom modules follow - appending now', this.#rmp.customModule);
    } else {
      this.#rmp.customModule = [];
    }
    this.#rmp.rmpUIManagement.showChrome();
    if (this.#rmp.ads) {
      this.#readyAds();
    } else {
      this.fire();
    }
  }

}
